import { sanitizeStudentHtml } from 'utils/safeHtml';
import React from 'react';
import {
  Box,
  Text,
  Flex,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Input,
  Textarea,
  useToast,
  TableContainer,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import xhr from 'xhr';
import fetchAllSchoolActivityRecords from 'utils/fetchAllSchoolActivityRecords';
import useAuth from 'hooks/useAuth';
import BasicModal from 'components/modal/Modal';
import {
  getTeacherPendingListCutoffIso,
  getTeacherAiFallbackCutoffIso,
} from 'api/profesores';

dayjs.extend(relativeTime);
dayjs.locale('es');

// Filtro de "realmente pendiente por calificar", igual al de la pantalla de
// profesores (Sittings.js) pero con scope de colegio (schoolFrom = schoolId).
// Sin esto se contaban/mostraban sittings que NO le tocan al colegio todavía
// (actividades con IA dentro de su ventana de calificación automática, pruebas
// aprobadas esperando su hora de publicación, o envíos de hace más de 90 días),
// lo que inflaba el total ("salen más actividades de las que son").
function pendingSittingsFilter(schoolId, subjectId) {
  const aiFallbackCutoffIso = getTeacherAiFallbackCutoffIso();

  const filter = {
    // Sin nota, o publicada por la IA y enviada a revisión del docente.
    $and: [
      {
        $or: [{ score: { $null: true } }, { review_requested: { $eq: true } }],
      },
      // Nota de una prueba aprobada que espera su hora de publicación: no le toca al colegio.
      {
        $or: [
          { publish_approved: { $null: true } },
          { publish_approved: { $eq: false } },
          { score: { $notNull: true } },
        ],
      },
    ],

    createdAt: { $gte: getTeacherPendingListCutoffIso() },

    student: { schoolFrom: { id: { $eq: schoolId } } },

    $or: [
      // Actividad sin IA: aparece normalmente.
      { activity: { ai: { $eq: false } } },
      // Actividades antiguas donde ai sea null.
      { activity: { ai: { $null: true } } },
      // Actividad con IA que ya pasó su corte y sigue sin calificación.
      { createdAt: { $lte: aiFallbackCutoffIso } },
      // La IA no pudo calificarla y la pasó al docente: aparece de inmediato.
      { ai_deferred_to_teacher: { $eq: true } },
      // Enviada con el flujo anterior (sin revisión previa): no entra a la IA.
      { precheck_result: { $null: true } },
      // Publicada y enviada a revisión desde el panel de calificación.
      { review_requested: { $eq: true } },
    ],
  };

  if (subjectId) filter.subject = { id: { $eq: subjectId } };

  return filter;
}

async function fetchSchoolSubjects(schoolId) {
  // 1. Get all class-groups for this school to find program IDs and types
  const classGroups = await fetchAllSchoolActivityRecords('/class-groups',
    {
      filters: { school: { id: { $eq: schoolId } } },
      fields: ['id'],
      populate: {
        grade: { fields: ['id'] },
        technical_career: { fields: ['id'] },
        course: { fields: ['id'] },
      },
    }
  );

  // Build program filters for subjects
  const programFilters = [];
  classGroups.forEach((cg) => {
    const attrs = cg.attributes || cg;
    const grade = attrs.grade?.data || attrs.grade;
    const tc = attrs.technical_career?.data || attrs.technical_career;
    const course = attrs.course?.data || attrs.course;
    if (grade?.id) programFilters.push({ grade: { id: { $eq: grade.id } } });
    if (tc?.id) programFilters.push({ technical_career: { id: { $eq: tc.id } } });
    if (course?.id) programFilters.push({ course: { id: { $eq: course.id } } });
  });

  if (programFilters.length === 0) return { subjects: [], totalCount: 0 };

  // 2. Get all subjects for those programs
  const subjectList = await fetchAllSchoolActivityRecords('/subjects',
    {
      filters: { $or: programFilters },
      fields: ['name'],
      populate: {
        course: { fields: ['name'] },
        grade: { fields: ['name'] },
        technical_career: { fields: ['name'] },
      },
    }
  );

  // 3. Get pending sittings counts (mismo filtro que la lista, para que el
  //    número del encabezado y los contadores por materia cuadren con lo que
  //    realmente se muestra).
  const sittings = await fetchAllSchoolActivityRecords('/activity-sittings',
    {
      filters: pendingSittingsFilter(schoolId),
      fields: ['id'],
      populate: { subject: { fields: ['id'] } },
    }
  );

  // Count per subject
  const countMap = {};
  let totalCount = 0;
  sittings.forEach((s) => {
    const subjId = s.subject?.id;
    if (!subjId) return;
    totalCount += 1;
    countMap[subjId] = (countMap[subjId] || 0) + 1;
  });

  // Merge: all subjects with their pending count
  const subjects = subjectList
    .map((s) => ({
      ...s,
      pendingCount: countMap[s.id] || 0,
    }))
    .sort((a, b) => b.pendingCount - a.pendingCount);

  return { subjects, totalCount };
}

async function fetchSittingsForSubject(schoolId, subjectId) {
  return fetchAllSchoolActivityRecords('/activity-sittings',
    {
      filters: pendingSittingsFilter(schoolId, subjectId),
      sort: ['createdAt:desc', 'id:desc'],
      populate: {
        student: { populate: ['user'] },
        subject: {
          populate: ['course', 'grade', 'technical_career'],
        },
        activity: true,
        file: true,
        files: true,
        class_group: true,
      },
    }
  );
}

export default function SchoolActividades() {
  const { user } = useAuth();
  const toast = useToast();

  const [subjects, setSubjects] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [selectedSubject, setSelectedSubject] = React.useState(null);
  const [sittings, setSittings] = React.useState([]);
  const [loadingSubjects, setLoadingSubjects] = React.useState(true);
  const [loadingSittings, setLoadingSittings] = React.useState(false);
  const [currentSitting, setCurrentSitting] = React.useState(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [score, setScore] = React.useState('');
  const [answer, setAnswer] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  // 1. Load subjects with counts
  React.useEffect(() => {
    const load = async () => {
      try {
        const schoolId = user?.school?.id;
        if (!schoolId) return;

        const { subjects: subjectList, totalCount: total } =
          await fetchSchoolSubjects(schoolId);

        setSubjects(subjectList);
        setTotalCount(total);

        // Auto-select first subject with pending
        if (subjectList.length > 0) {
          setSelectedSubject(subjectList[0]);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingSubjects(false);
      }
    };

    load();
  }, [user?.school?.id]);

  // 2. Load sittings when subject changes
  React.useEffect(() => {
    if (!selectedSubject || !user?.school?.id) {
      setSittings([]);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoadingSittings(true);
      try {
        const data = await fetchSittingsForSubject(
          user.school.id,
          selectedSubject.id
        );
        if (!cancelled) setSittings(data);
      } catch (error) {
        console.error(error);
        if (!cancelled) setSittings([]);
      } finally {
        if (!cancelled) setLoadingSittings(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [selectedSubject?.id, user?.school?.id]);

  const getSubjectProgram = (subject) => {
    if (!subject) return '';
    if (subject.course) return subject.course.name;
    if (subject.grade) return subject.grade.name;
    if (subject.technical_career) return subject.technical_career.name;
    return '';
  };

  const handleSelectSubject = (subj) => {
    setSelectedSubject(subj);
  };

  const removeSitting = (sittingId) => {
    setSittings((prev) => prev.filter((s) => s.id !== sittingId));

    // Update counts
    setSubjects((prev) => {
      const updated = prev
        .map((s) => {
          if (s.id !== selectedSubject?.id) return s;
          return { ...s, pendingCount: Math.max(0, s.pendingCount - 1) };
        })
        .sort((a, b) => b.pendingCount - a.pendingCount);

      // Update selected subject count too
      setSelectedSubject((sel) =>
        sel
          ? { ...sel, pendingCount: Math.max(0, sel.pendingCount - 1) }
          : null
      );

      return updated;
    });

    setTotalCount((prev) => Math.max(0, prev - 1));
  };

  const handleGrade = async () => {
    try {
      if (!score || !answer)
        return alert('Debes completar la nota y la retroalimentación');
      if (score < 0 || score > 10)
        return alert('La nota debe ser entre 0 y 10');

      setSaving(true);

      await xhr.put(`/activity-sittings/${currentSitting.id}`, {
        data: {
          score: Number(score),
          answer,
          user: currentSitting.student?.user,
          activity: currentSitting.activity,
        },
      });

      removeSitting(currentSitting.id);
      setIsOpen(false);
      setScore('');
      setAnswer('');
      setCurrentSitting(null);

      toast({
        title: 'Actividad calificada correctamente',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const confirmed = window.confirm(
        '¿Estás seguro de que deseas eliminar esta actividad?'
      );
      if (!confirmed) return;

      setSaving(true);
      await xhr.delete(`/activity-sittings/${currentSitting.id}`);

      removeSitting(currentSitting.id);
      setIsOpen(false);
      setScore('');
      setAnswer('');
      setCurrentSitting(null);

      toast({
        title: 'Actividad eliminada correctamente',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loadingSubjects) {
    return (
      <Flex justify="center" mt={10}>
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box>
      <Flex align="center" gap={2} mb={4}>
        <Text fontSize="28px" fontWeight="bold">
          Actividades por calificar
        </Text>
        <Text fontSize="28px" fontWeight="bold" color="red.500">
          ({totalCount})
        </Text>
      </Flex>

      {/* Subject filter */}
      <Flex align="center" mb={5}>
        <Menu>
          <MenuButton as={Button} rightIcon={<ChevronDownIcon />}>
            <Flex align="center">
              {selectedSubject ? (
                <>
                  {selectedSubject.name}
                  {selectedSubject.pendingCount > 0 && (
                    <Text color="red.500" ml={2}>
                      [{selectedSubject.pendingCount}]
                    </Text>
                  )}
                </>
              ) : (
                'Seleccionar materia'
              )}
            </Flex>
          </MenuButton>
          <MenuList maxH="300px" overflowY="auto">
            {subjects.map((subj) => (
              <MenuItem
                key={subj.id}
                onClick={() => handleSelectSubject(subj)}
                fontWeight={
                  subj.id === selectedSubject?.id ? 'bold' : 'normal'
                }
              >
                <Flex>
                  {subj.name}
                  {subj.pendingCount > 0 && (
                    <Text color="red.500" ml={2}>
                      [{subj.pendingCount}]
                    </Text>
                  )}
                </Flex>
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </Flex>

      {!selectedSubject ? (
        <Text color="gray.500">
          Selecciona una materia para ver las actividades pendientes.
        </Text>
      ) : loadingSittings ? (
        <Flex justify="center" mt={6}>
          <Spinner size="lg" />
        </Flex>
      ) : sittings.length === 0 ? (
        <Text color="gray.500">
          No hay actividades pendientes en esta materia.
        </Text>
      ) : (
        <TableContainer>
          <Table>
            <Thead bg="gray.200">
              <Tr>
                <Th>Estudiante</Th>
                <Th>Código</Th>
                <Th>Materia</Th>
                <Th>Programa</Th>
                <Th>Grupo</Th>
                <Th>Fecha</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {sittings.map((sitting) => {
                const diffDate = dayjs().diff(sitting.createdAt, 'day');
                return (
                  <Tr
                    key={sitting.id}
                    color={diffDate > 1 ? 'red.500' : undefined}
                  >
                    <Td>
                      {sitting.student?.user?.first_name}{' '}
                      {sitting.student?.user?.last_name}
                    </Td>
                    <Td>{sitting.student?.id}</Td>
                    <Td>{sitting.subject?.name}</Td>
                    <Td>{getSubjectProgram(sitting.subject)}</Td>
                    <Td>{sitting.class_group?.name}</Td>
                    <Td>{dayjs(sitting.createdAt).fromNow()}</Td>
                    <Td>
                      <Button
                        size="sm"
                        colorScheme="purple"
                        onClick={() => {
                          setCurrentSitting(sitting);
                          setScore('');
                          setAnswer('');
                          setIsOpen(true);
                        }}
                      >
                        Ver
                      </Button>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      <BasicModal isOpen={isOpen} setIsOpen={setIsOpen} size="3xl">
        {currentSitting && (
          <Box>
            <Flex justify="space-between" align="start" mb={4}>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Materia: {currentSitting.subject?.name}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Programa: {getSubjectProgram(currentSitting.subject)}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Grupo: {currentSitting.class_group?.name}
                </Text>
              </Box>
              <Button
                size="xs"
                colorScheme="red"
                onClick={handleDelete}
                isLoading={saving}
              >
                Eliminar actividad
              </Button>
            </Flex>

            {/* Activity content */}
            <Box mb={4} p={3} bg="gray.50" borderRadius="md">
              <Text fontWeight="bold" mb={2}>
                Actividad
              </Text>
              <Box
                dangerouslySetInnerHTML={{
                  __html: currentSitting.activity?.content,
                }}
              />
            </Box>

            {/* Student response */}
            <Box mb={4}>
              <Text fontSize="lg" fontWeight="bold" mb={2}>
                Respuesta de {currentSitting.student?.user?.first_name}{' '}
                {currentSitting.student?.user?.last_name} (Cod.{' '}
                {currentSitting.student?.id})
              </Text>
              <Text fontSize="sm" color="gray.500" mb={2}>
                Enviado el:{' '}
                {dayjs(currentSitting.createdAt).format('DD-MM-YYYY hh:mm A')}
              </Text>

              {currentSitting.file || currentSitting.files?.length ? (
                <Box>
                  <Text mb={1}>El estudiante envió un archivo:</Text>
                  {currentSitting.file ? (
                    <a
                      style={{ color: 'blue' }}
                      href={currentSitting.file.url}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {currentSitting.file.name}
                    </a>
                  ) : (
                    currentSitting.files?.map((f, i) => (
                      <Box key={i}>
                        <a
                          style={{ color: 'blue' }}
                          href={f.url}
                          rel="noreferrer"
                          target="_blank"
                        >
                          {f.name || 'Ver archivo'}
                        </a>
                      </Box>
                    ))
                  )}
                </Box>
              ) : (
                <Box
                  p={3}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeStudentHtml(currentSitting.content),
                  }}
                />
              )}
            </Box>

            {/* Grading */}
            <Box>
              <Text mb={1}>Nota (de 0 a 10)</Text>
              <Input
                type="number"
                value={score}
                onChange={(e) => setScore(e.target.value)}
                placeholder="Ej: 8.5"
                mb={3}
                max={10}
                min={0}
              />

              <Text mb={1}>Retroalimentación</Text>
              <Textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Escribe tu retroalimentación para el estudiante..."
                rows={4}
                mb={4}
              />

              <Button
                colorScheme="purple"
                bg="#574feb"
                onClick={handleGrade}
                isLoading={saving}
              >
                Calificar
              </Button>
            </Box>
          </Box>
        )}
      </BasicModal>
    </Box>
  );
}
